import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useApp } from '@/app/state';
import { MODE_HOME, getAutoPrint, getDeviceMode, setAutoPrint, setDeviceMode, type DeviceMode } from '@/app/device';
import { Button, Field, Input, cx } from '@/components/ui';
import { nowIso } from '@/lib/db/db';

const MODES: DeviceMode[] = ['reception', 'doctor', 'pharmacy', 'board', 'full'];
const ICON: Record<DeviceMode, string> = { reception: '🪪', doctor: '🩺', pharmacy: '💊', board: '📺', full: '🗂️' };

export function DeviceSetupPage() {
  const { t, i18n } = useTranslation();
  const { db, session, hasRole } = useApp();
  const nav = useNavigate();
  const [mode, setMode] = useState<DeviceMode>(getDeviceMode() ?? 'reception');
  const [name, setName] = useState('');
  const [print, setPrint] = useState(getAutoPrint());

  async function save() {
    setDeviceMode(mode); setAutoPrint(print);
    if (db && session?.deviceId && name.trim()) {
      await db.execute('update devices set name = ?, is_token_issuer = ?, updated_at = ? where id = ?', [name.trim(), mode === 'reception' || mode === 'full' ? 1 : 0, nowIso(), session.deviceId]);
    }
    nav(MODE_HOME[mode], { replace: true });
  }

  const modes = hasRole('owner') ? MODES : MODES.filter((m) => m !== 'full');
  return (
    <div className={cx('min-h-dvh flex items-center justify-center p-6', i18n.language === 'my' && 'font-my')}>
      <div className="w-full max-w-2xl">
        <h1 className="text-3xl font-bold">{t('device.title')}</h1>
        <p className="mb-6 text-ink-2">{t('device.body')}</p>
        <div className="grid gap-3 sm:grid-cols-2">
          {modes.map((m) => (
            <button key={m} onClick={() => setMode(m)} className={cx('rounded-2xl border p-5 text-left transition', mode === m ? 'border-accent bg-accent-soft' : 'border-rule bg-surface hover:bg-accent-soft/50')}>
              <div className="text-3xl">{ICON[m]}</div>
              <div className="mt-2 text-xl font-bold">{t(`device.${m}`)}</div>
              <div className="text-sm text-ink-2">{t(`device.${m}Hint`)}</div>
            </button>
          ))}
        </div>
        <div className="mt-5 grid gap-3">
          <Field label={t('device.name')}><Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('device.namePlaceholder')} /></Field>
          {(mode === 'reception' || mode === 'full') && (
            <label className="flex items-center gap-2"><input type="checkbox" className="h-5 w-5" checked={print} onChange={(e) => setPrint(e.target.checked)} />{t('device.autoPrint')}</label>
          )}
          <Button variant="primary" className="text-lg" onClick={save}>{t('device.save')}</Button>
        </div>
      </div>
    </div>
  );
}
